import React, { useState, useEffect } from 'react';
import { useSuperAdmin } from '../contexts/SuperAdminContext';
import {
  Users,
  Building2,
  Activity,
  Settings,
  AlertTriangle,
  Database,
  Eye,
  Edit,
  Trash2,
  Play,
  Pause,
  Plus,
  BarChart3,
  DollarSign,
  Server
} from 'lucide-react';

const SuperAdminDashboard = () => {
  const {
    allTenants,
    systemStats,
    loading,
    createTenant,
    deleteTenant,
    suspendTenant,
    activateTenant,
    getTenantData,
    getTenantUsers,
    getTenantAnalytics
  } = useSuperAdmin();

  const [activeTab, setActiveTab] = useState('overview');
  const [showCreateTenant, setShowCreateTenant] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tenantData, setTenantData] = useState({});
  const [, setTenantUsers] = useState([]);
  const [, setTenantAnalytics] = useState({});

  const [newTenantData, setNewTenantData] = useState({
    name: '',
    slug: '',
    email: '',
    subscription_plan: 'starter',
    user_limit: 5,
    location_limit: 2,
    features: ['inventory', 'sales', 'customers']
  });

  useEffect(() => {
    if (selectedTenant) {
      loadTenantDetails(selectedTenant.id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTenant]);

  const loadTenantDetails = async (tenantId) => {
    try {
      const [data, users, analytics] = await Promise.all([
        getTenantData(tenantId, 'overview'),
        getTenantUsers(tenantId),
        getTenantAnalytics(tenantId, 'monthly')
      ]);
      
      setTenantData(data);
      setTenantUsers(users);
      setTenantAnalytics(analytics);
    } catch (error) {
      console.error('Error loading tenant details:', error);
    }
  };

  const handleCreateTenant = async (e) => {
    e.preventDefault();
    try {
      await createTenant(newTenantData);
      setShowCreateTenant(false);
      setNewTenantData({
        name: '',
        slug: '',
        email: '',
        subscription_plan: 'starter',
        user_limit: 5,
        location_limit: 2,
        features: ['inventory', 'sales', 'customers']
      });
    } catch (error) {
      console.error('Error creating tenant:', error);
    }
  };

  const handleTenantAction = async (tenantId, action, data = {}) => {
    try {
      switch (action) {
        case 'suspend':
          await suspendTenant(tenantId, data);
          break;
        case 'activate':
          await activateTenant(tenantId);
          break;
        case 'delete':
          if (window.confirm('Are you sure you want to delete this tenant? This action cannot be undone.')) {
            await deleteTenant(tenantId);
          }
          break;
        default:
          break;
      }
    } catch (error) {
      console.error(`Error performing ${action} on tenant:`, error);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-md mb-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Super Admin Dashboard</h1>
              <p className="text-gray-600">Manage all tenants and system operations</p>
            </div>
            <button
              onClick={() => setShowCreateTenant(true)}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create New Tenant
            </button>
          </div>
        </div>

        {/* System Stats */}
        <div className="px-6 py-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-center">
                <Building2 className="w-8 h-8 text-blue-600" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-blue-600">Total Tenants</p>
                  <p className="text-2xl font-bold text-blue-900">{systemStats.total_tenants || allTenants.length}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="flex items-center">
                <Users className="w-8 h-8 text-green-600" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-green-600">Active Users</p>
                  <p className="text-2xl font-bold text-green-900">{systemStats.total_users || 0}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-yellow-50 p-4 rounded-lg">
              <div className="flex items-center">
                <Activity className="w-8 h-8 text-yellow-600" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-yellow-600">Active Tenants</p>
                  <p className="text-2xl font-bold text-yellow-900">{systemStats.active_tenants || 0}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-purple-50 p-4 rounded-lg">
              <div className="flex items-center">
                <DollarSign className="w-8 h-8 text-purple-600" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-purple-600">Monthly Revenue</p>
                  <p className="text-2xl font-bold text-purple-900">${systemStats.monthly_revenue || 0}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-md mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {[
              { id: 'overview', name: 'Overview', icon: BarChart3 },
              { id: 'tenants', name: 'Tenants', icon: Building2 },
              { id: 'users', name: 'Users', icon: Users },
              { id: 'system', name: 'System', icon: Server },
              { id: 'monitoring', name: 'Monitoring', icon: Activity }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="w-4 h-4 mr-2" />
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">System Overview</h3>
              
              {/* Quick Actions */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <button className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 text-left">
                  <Database className="w-8 h-8 text-blue-600 mb-2" />
                  <h4 className="font-medium text-gray-900">System Backup</h4>
                  <p className="text-sm text-gray-600">Create system-wide backup</p>
                </button>
                
                <button className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 text-left">
                  <AlertTriangle className="w-8 h-8 text-yellow-600 mb-2" />
                  <h4 className="font-medium text-gray-900">System Alerts</h4>
                  <p className="text-sm text-gray-600">View system alerts</p>
                </button>
                
                <button className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 text-left">
                  <Settings className="w-8 h-8 text-gray-600 mb-2" />
                  <h4 className="font-medium text-gray-900">System Settings</h4>
                  <p className="text-sm text-gray-600">Configure system options</p>
                </button>
              </div>

              {/* Recent Activity */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Recent Activity</h4>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600">No recent activity to display</p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'tenants' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">All Tenants</h3>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    placeholder="Search tenants..."
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Tenants Table */}
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Tenant
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Plan
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Users
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {allTenants.map((tenant) => (
                      <tr key={tenant.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                                <span className="text-sm font-medium text-blue-600">
                                  {tenant.name.charAt(0).toUpperCase()}
                                </span>
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">{tenant.name}</div>
                              <div className="text-sm text-gray-500">{tenant.slug}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            tenant.status === 'active' ? 'bg-green-100 text-green-800' :
                            tenant.status === 'trial' ? 'bg-blue-100 text-blue-800' :
                            tenant.status === 'suspended' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {tenant.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {tenant.subscription_plan}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {tenant.users_count || 0} / {tenant.user_limit === -1 ? '∞' : tenant.user_limit}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(tenant.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => setSelectedTenant(tenant)}
                              className="text-blue-600 hover:text-blue-900"
                              title="View Details"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => {/* Edit tenant */}}
                              className="text-indigo-600 hover:text-indigo-900"
                              title="Edit"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            {tenant.status === 'active' ? (
                              <button
                                onClick={() => handleTenantAction(tenant.id, 'suspend')}
                                className="text-yellow-600 hover:text-yellow-900"
                                title="Suspend"
                              >
                                <Pause className="w-4 h-4" />
                              </button>
                            ) : (
                              <button
                                onClick={() => handleTenantAction(tenant.id, 'activate')}
                                className="text-green-600 hover:text-green-900"
                                title="Activate"
                              >
                                <Play className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => handleTenantAction(tenant.id, 'delete')}
                              className="text-red-600 hover:text-red-900"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'users' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">All Users</h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600">User management interface coming soon...</p>
              </div>
            </div>
          )}

          {activeTab === 'system' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">System Management</h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600">System configuration interface coming soon...</p>
              </div>
            </div>
          )}

          {activeTab === 'monitoring' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">System Monitoring</h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600">Monitoring dashboard coming soon...</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create Tenant Modal */}
      {showCreateTenant && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Create New Tenant</h3>
              <form onSubmit={handleCreateTenant} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Organization Name</label>
                  <input
                    type="text"
                    value={newTenantData.name}
                    onChange={(e) => setNewTenantData({ ...newTenantData, name: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Slug</label>
                  <input
                    type="text"
                    value={newTenantData.slug}
                    onChange={(e) => setNewTenantData({ ...newTenantData, slug: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    value={newTenantData.email}
                    onChange={(e) => setNewTenantData({ ...newTenantData, email: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Subscription Plan</label>
                  <select
                    value={newTenantData.subscription_plan}
                    onChange={(e) => setNewTenantData({ ...newTenantData, subscription_plan: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="starter">Starter</option>
                    <option value="professional">Professional</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                </div>
                
                <div className="flex space-x-3">
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Create Tenant
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowCreateTenant(false)}
                    className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Tenant Details Sidebar */}
      {selectedTenant && (
        <div className="fixed inset-y-0 right-0 w-96 bg-white shadow-lg z-40 overflow-y-auto">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Tenant Details</h3>
              <button
                onClick={() => setSelectedTenant(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <span className="sr-only">Close</span>
                ×
              </button>
            </div>
            
            <div className="space-y-6">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Basic Information</h4>
                <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                  <div>
                    <span className="text-sm font-medium text-gray-700">Name:</span>
                    <span className="ml-2 text-sm text-gray-900">{selectedTenant.name}</span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-700">Slug:</span>
                    <span className="ml-2 text-sm text-gray-900">{selectedTenant.slug}</span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-700">Status:</span>
                    <span className={`ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      selectedTenant.status === 'active' ? 'bg-green-100 text-green-800' :
                      selectedTenant.status === 'trial' ? 'bg-blue-100 text-blue-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {selectedTenant.status}
                    </span>
                  </div>
                </div>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Usage Statistics</h4>
                <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                  <div>
                    <span className="text-sm font-medium text-gray-700">Users:</span>
                    <span className="ml-2 text-sm text-gray-900">
                      {tenantData.users_count || 0} / {selectedTenant.user_limit === -1 ? '∞' : selectedTenant.user_limit}
                    </span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-700">Locations:</span>
                    <span className="ml-2 text-sm text-gray-900">
                      {tenantData.locations_count || 0} / {selectedTenant.location_limit === -1 ? '∞' : selectedTenant.location_limit}
                    </span>
                  </div>
                </div>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Quick Actions</h4>
                <div className="space-y-2">
                  <button className="w-full px-3 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700">
                    View Full Details
                  </button>
                  <button className="w-full px-3 py-2 text-sm bg-gray-600 text-white rounded-md hover:bg-gray-700">
                    Export Data
                  </button>
                  <button className="w-full px-3 py-2 text-sm bg-yellow-600 text-white rounded-md hover:bg-yellow-700">
                    Backup Data
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminDashboard;
